import {
  fetchPolicyExtractedDataByRequestIdRequest,
  postPolicyDocumentExtractRequest,
  postPresignedUrlForClientByClientIdRequest,
  resetPolicyExtractorState,
  selectClient,
  selectPolicyExtractor,
  setStatus,
} from '@client/model';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePolling } from '@client/hooks/usePolling';
import { PdfFileUploader } from './PdfFileUploader';
import { uploadFileToS3 } from '@client/util/uploadFileToBucket';
import { Button } from './ui/button';
import { PolicyAnalysisErrorAlert, PolicyAnalysisTable } from './PolicyExtractorResults';
import { PolicyExtractorProgressBar } from './PolicyExtractorProgressBar';
import { getCoupleLtcStartData } from '@shared/coupleLtcStartData';
import { newUuid } from '@shared';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';

type PolicyUploadProps = {
  resetState: () => void;
  policyFile: File | null;
  setPolicyFile: (file: File | null) => void;
};

function PolicyUpload({ resetState, policyFile, setPolicyFile }: PolicyUploadProps) {
  const [showRemoveFileWarning, setShowRemoveFileWarning] = useState(false);
  const client = useSelector(selectClient);
  const { status, processedResponse, errors, requestId } = useSelector(selectPolicyExtractor);
  const maybeImageBasedPdfError = errors.length > 0 && errors[0] === 'Extracted text is too short';
  const isLoading = status === 'initiated' || status === 'polling';
  const showUploadButton = policyFile && !isLoading && !processedResponse;
  const showProgressBar = isLoading && !processedResponse;
  const dispatch = useDispatch();

  useDispatchPolicyExtractRunEffect();
  useUploadFileEffect(policyFile);

  usePolling({
    pollingFunction: () => {
      dispatch(fetchPolicyExtractedDataByRequestIdRequest({ requestId }));
    },
    shouldPoll: status === 'polling',
    interval: 5000,
    dependencies: [requestId],
  });

  function handleFileUpload() {
    if (maybeImageBasedPdfError) {
      setPolicyFile(null);
      dispatch(resetPolicyExtractorState());
      return;
    }

    if (!policyFile) return;
    dispatch(
      postPresignedUrlForClientByClientIdRequest({
        clientId: client.clientId,
        originalFileName: policyFile.name,
      }),
    );
  }
  const onPolicyFileChange = (file: File | null) => {
    if (file) {
      setPolicyFile(file);
      setShowRemoveFileWarning(false);
    } else {
      // if file is removed and there are processed results, show warning
      if (processedResponse) {
        setShowRemoveFileWarning(true);
      } else {
        resetState();
      }
    }
  };

  return (
    <div className="mb-12 flex flex-col gap-4">
      <div
        className={`flex flex-col gap-4 ${policyFile ? 'border-primary-200 rounded-lg border p-5' : ''}`}
      >
        <PdfFileUploader
          file={policyFile}
          setFile={onPolicyFileChange}
          maxFileSizeInMB={10}
          hideRemove={isLoading}
        />
        {showProgressBar && <PolicyExtractorProgressBar expectedCompleteDurationSeconds={45} />}
      </div>
      <PolicyAnalysisErrorAlert />
      {errors.length === 0 && <PolicyAnalysisTable />}
      <UnsavedChangesDialog
        showWarning={showRemoveFileWarning}
        handleCancelClose={() => setShowRemoveFileWarning(false)}
        handleConfirmClose={() => {
          setShowRemoveFileWarning(false);
          resetState();
        }}
        primaryButtonText="Remove File"
        secondaryButtonText="Keep File"
        dialogTitle="Remove Policy File"
        dialogDescription="If you remove the file, you will lose all the upload results and any changes you have made. Are you sure you wish to remove the file?"
      />
      {showUploadButton && (
        <Button
          className="w-full rounded-full font-medium"
          onClick={handleFileUpload}
        >
          {maybeImageBasedPdfError
            ? 'Upload New PDF'
            : status === 'error'
              ? 'Try again'
              : 'Analyze Policy'}
        </Button>
      )}
    </div>
  );
}

export default PolicyUpload;

function useUploadFileEffect(policyDocumentFile: File | null) {
  const dispatch = useDispatch();
  const { fileUrl, fileBucket, fileKey } = useSelector(selectPolicyExtractor);
  const isPresignedUrlReady = fileUrl && fileBucket && fileKey;

  return useEffect(() => {
    const uploadFile = async () => {
      if (isPresignedUrlReady && policyDocumentFile) {
        await uploadFileToS3(fileUrl, policyDocumentFile, 'application/pdf');
        dispatch(setStatus('fileUploadComplete'));
      }
    };
    uploadFile();
  }, [isPresignedUrlReady, policyDocumentFile]);
}

function useDispatchPolicyExtractRunEffect() {
  // dispatch a policy extract run when the file is uploaded
  const dispatch = useDispatch();
  const { status, fileBucket, fileKey } = useSelector(selectPolicyExtractor);
  const client = useSelector(selectClient);
  const {
    clientId,
    intakeSurvey: { clientBirthYear, clientGenderAtBirth },
    appliedInferenceSet: { ltcAtAge },
    clientFullName,
    mutableClientPartner,
  } = client;
  const coupleLtcStartData = getCoupleLtcStartData(client, mutableClientPartner);

  const requestId = newUuid();
  const requestBody: PolicyUploadTextAnalysisRequest = {
    fileBucket,
    fileKey,
    clientId,
    requestId,
    ltcAtAge,
    clientBirthYear,
    clientFullName,
    clientGenderAtBirth,
    policyActivationAgeClientIsPolicyHolder: coupleLtcStartData
      ? coupleLtcStartData.getPolicyHolderAgeAtPolicyActivation(true)
      : undefined,
    policyActivationAgePartnerIsPolicyHolder: coupleLtcStartData
      ? coupleLtcStartData.getPolicyHolderAgeAtPolicyActivation(false)
      : undefined,
  };

  return useEffect(() => {
    if (status === 'fileUploadComplete') {
      dispatch(postPolicyDocumentExtractRequest(requestBody));
    }
  }, [status]);
}
